import { getFeatureAccessCompaniesRequest, parseFeatureAccessError } from 'api/requests/feature-access-requests';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FEATURE_ACCESS__LOAD_ERROR } from 'translations/constants';
import { getFeatureAccessErrorKey, IFeatureAccessRow, toFeatureAccessRow } from '../utils';

export interface UseFeatureAccessOverviewResult {
  rows: IFeatureAccessRow[];
  hasLoaded: boolean;
  isLoading: boolean;
  loadErrorKey: string | null;
  reload: () => Promise<void>;
}

/**
 * Loads every Company of the agency at once (client mode, about 900 rows at most). A save and a close
 * can each trigger a reload in quick succession, so only the latest request's answer is applied.
 */
export const useFeatureAccessOverview = (): UseFeatureAccessOverviewResult => {
  const [rows, setRows] = useState<IFeatureAccessRow[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadErrorKey, setLoadErrorKey] = useState<string | null>(null);
  const latestRequest = useRef(0);

  const reload = useCallback(async () => {
    latestRequest.current += 1;
    const requestId = latestRequest.current;
    setIsLoading(true);
    try {
      const companies = await getFeatureAccessCompaniesRequest();
      if (requestId !== latestRequest.current) return;
      setRows(companies.map(toFeatureAccessRow));
      setLoadErrorKey(null);
      setHasLoaded(true);
    } catch (error) {
      if (requestId !== latestRequest.current) return;
      setLoadErrorKey(getFeatureAccessErrorKey(parseFeatureAccessError(error), FEATURE_ACCESS__LOAD_ERROR));
    } finally {
      if (requestId === latestRequest.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return { rows, hasLoaded, isLoading, loadErrorKey, reload };
};
